import React, {
  useState,
  useCallback,
  forwardRef,
  useImperativeHandle,
  useEffect,
  useRef,
} from 'react';
import AvailableObjects, {
  specialObjects,
} from './available-objects/AvailableObjects';
import CodingFormEditor from './coding-form-editor/CodingFormEditor';
import Grid from '@mui/material/Grid';
import './CreateEditCodingForm.scss';
import {useCodingFormMetadata} from '../../../hooks/document/useDocument';
import {ICodingFormDetailsForEditResponse} from '../../../api/types/document.type';
import UnsavedChangesModal from './unsaved-changes-modal/UnsavedChangesModal';
import {CodingFormItem, Id} from '../../../shared/types';
import {useDispatch} from 'react-redux';
import {setMetaData} from '../../../redux/slice/codingFormSlice';

export interface CreateEditCodingFormHandle {
  save: () => Promise<void>;
  resetDirty?: () => void;
  handleClose: () => void;
  forceReset: () => void;
}

interface CreateEditCodingFormProps {
  codingForm?: ICodingFormDetailsForEditResponse['data'];
  onClose?: () => void;
  onSave?: () => Promise<void> | void;
  onChangeStatus?: (hasChanges: boolean) => void;
}

const CreateEditCodingForm = forwardRef<
  CreateEditCodingFormHandle,
  CreateEditCodingFormProps
>((props, ref) => {
  const {codingForm, onClose, onSave, onChangeStatus} = props;
  const {data: metaDataList, isLoading: isMetaDataLoading} =
    useCodingFormMetadata();

  const [tagDataList, setTagDataList] = useState(
    () => metaDataList?.data?.tagDataList ?? []
  );
  const [commentList, setCommentList] = useState(
    () => metaDataList?.data?.commentList ?? []
  );
  const [metadataFieldList, setMetadataFieldList] = useState(
    () => metaDataList?.data?.metadataFieldList ?? []
  );

  const dispatch = useDispatch();

  useEffect(() => {
    if (metaDataList?.data) {
      dispatch(setMetaData(metaDataList.data));
      setTagDataList(metaDataList.data.tagDataList ?? []);
      setCommentList(metaDataList.data.commentList ?? []);
      setMetadataFieldList(metaDataList.data.metadataFieldList ?? []);

      // If we have a codingForm, mark its items as disabled in the lists
      if (codingForm?.codingFormControls) {
        const ids = {
          tags: [] as string[],
          comments: [] as string[],
          fields: [] as string[],
          specialObjects: [] as string[],
        };

        codingForm.codingFormControls.forEach((control) => {
          const id = control.systemControlName?.split('_')[1];
          if (id) {
            switch (control.contextType.toLowerCase()) {
              case 'tag':
                ids.tags.push(id);
                break;
              case 'comment':
                ids.comments.push(id);
                break;
              case 'field':
                ids.fields.push(id);
                break;
            }
          }
        });

        // Mark items as disabled
        if (ids.tags.length) {
          setTagDataList((prev) => markDisabled(prev, ids.tags, 'id'));
        }
        if (ids.comments.length) {
          setCommentList((prev) =>
            markDisabled(prev, ids.comments, 'commentId')
          );
        }
        if (ids.fields.length) {
          setMetadataFieldList((prev) =>
            markDisabled(prev, ids.fields, 'projectFieldID')
          );
        }
      }
    }
  }, [metaDataList, codingForm, dispatch]);

  const [hasChanges, setHasChanges] = useState(false);
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const getControlFromAPI = useCallback(
    (type: string, id: string) => {
      return codingForm?.codingFormControls?.find(
        (c) =>
          c.contextType.toUpperCase() === type &&
          c.systemControlName?.split('_')[1] === id
      );
    },
    [codingForm]
  );

  const [selectedObjects, setSelectedObjects] = useState<CodingFormItem[]>(
    () => {
      if (codingForm?.codingFormControls) {
        return codingForm.codingFormControls.map((control, index) => {
          let groupAssociation = null;
          if (control.groupControlName) {
            // Try to find the group in RADIOGROUP or CHECKGROUP
            const allGroups = [
              ...(codingForm.codingFormControls.filter(
                (c) => c.contextType === 'RADIOGROUP'
              ) || []),
              ...(codingForm.codingFormControls.filter(
                (c) => c.contextType === 'CHECKGROUP'
              ) || []),
            ];
            const found = allGroups.find(
              (g) => g.systemControlName === control.groupControlName
            );
            if (found) {
              groupAssociation = {
                value: found.systemControlName,
                label: `${found.customControlLabel || ''} (${found.systemControlName})`,
              };
            } else {
              groupAssociation = {
                value: control.groupControlName,
                label: control.groupControlName,
              };
            }
          }
          return {
            id: index,
            cfItemID: Number(control.systemControlName.split('_')[1]),
            sequence: control.sequence,
            type: control.contextType,
            specialFlag: control.isSpecial || false,
            value: control.systemControlName.split('_')[1],
            systemLabel: control.systemControlName,
            friendlyLabel: control.customControlLabel,
            logicRuleCount: control.logicRuleCount,
            tagType: control.codingFormControlTypeCode,
            errorMessage: control.errorMessage,
            defaultAction: control.defaultAction,
            helpText: control.helpText,
            fieldLogic: JSON.parse(control.controlLogicJSon || '[]'),
            fieldLogicJson: control.controlLogicJSon,
            fieldLogicAction: control.fieldLogicAction,
            fieldLogicObjectLabel: `${control.customControlLabel} (${control.systemControlName})`,
            groupAssociation,
            displayType: control.displayType,
            metaDataType: '',
            maxLength: control.maxLength,
            minValue: control.minValue || '',
            maxValue: control.maxValue || '',
            splitLength: control.splitLength,
            dataTypeLengthCode: '',
            isCollapsed: true,
          };
        });
      }
      return [];
    }
  );
  const handleAddToForm = async (selectedItems: {
    tags: string[] | null;
    comments: string[] | null;
    metaDatas: string[] | null;
    specialObjects: string[] | null;
  }) => {
    const newItems: CodingFormItem[] = [];
    const existingIds = selectedObjects.map((item) => item.cfItemID);
    let nextId = existingIds.length > 0 ? Math.max(...existingIds) + 1 : 0;

    // Convert tags
    if (selectedItems.tags?.length) {
      selectedItems.tags.forEach((tagId) => {
        const tag = metaDataList?.data?.tagDataList.find(
          (t) => t.id.toString() === tagId
        );
        if (tag) {
          // Check if this tag exists in the API response
          const apiControl = getControlFromAPI('TAG', String(tag.id));
          newItems.push({
            id: nextId,
            cfItemID: nextId,
            sequence: nextId,
            type: 'TAG',
            specialFlag: false,
            value: String(tag.id), // Store the ID instead of name to match with tag list
            systemLabel: `TAG_${tag.id}`,
            friendlyLabel: tag.name,
            logicRuleCount: apiControl?.logicRuleCount || '0',
            tagType: apiControl?.codingFormControlTypeCode || 'CHECK', // Default to CHECK for tags
            errorMessage: apiControl?.errorMessage || '',
            defaultAction: apiControl?.defaultAction || 'OPTIONAL', // Default to OPTIONAL
            helpText: apiControl?.helpText || '',
            fieldLogic: [],
            fieldLogicJson: apiControl?.controlLogicJSon || '[]',
            fieldLogicAction: apiControl?.fieldLogicAction || '',
            fieldLogicObjectLabel: `${tag.name} (TAG_${tag.id})`,
            groupAssociation: apiControl?.groupControlName
              ? {
                  value: apiControl.groupControlName,
                  label:
                    apiControl.customControlLabel ||
                    apiControl.groupControlName,
                }
              : null,
            // ? {
            //     value: apiControl.groupControlName,
            //     label:
            //       apiControl.customControlLabel ||
            //       apiControl.groupControlName,
            //   }
            // : null,
            displayType: apiControl?.displayType || 'DEFAULT',
          });
          nextId++;
        }
      });
      setTagDataList((prev) =>
        markDisabled(prev, selectedItems.tags ?? [], 'id')
      );
    }

    // Convert comments
    if (selectedItems.comments?.length) {
      selectedItems.comments.forEach((commentId) => {
        const comment = metaDataList?.data?.commentList.find(
          (c) => c.commentId.toString() === commentId
        );
        if (comment) {
          // Check if this comment exists in the API response
          const apiControl = getControlFromAPI(
            'COMMENT',
            String(comment.commentId)
          );
          newItems.push({
            id: nextId,
            cfItemID: nextId,
            sequence: nextId,
            type: 'COMMENT',
            specialFlag: false,
            value: String(comment.commentId),
            systemLabel: `COMMENT_${comment.commentId}`,
            friendlyLabel: comment.commentLabel,
            logicRuleCount: apiControl?.logicRuleCount || '0',
            tagType: apiControl?.codingFormControlTypeCode || '',
            errorMessage: apiControl?.errorMessage || '',
            defaultAction: apiControl?.defaultAction || 'OPTIONAL',
            helpText: apiControl?.helpText || '',
            fieldLogic: [],
            fieldLogicJson: apiControl?.controlLogicJSon || '[]',
            fieldLogicAction: apiControl?.fieldLogicAction || '',
            fieldLogicObjectLabel: `${comment.commentLabel} (COMMENT_${comment.commentId})`,
            groupAssociation: apiControl?.groupControlName
              ? {
                  value: apiControl.groupControlName,
                  label:
                    apiControl.customControlLabel ||
                    apiControl.groupControlName,
                }
              : null,
            displayType: apiControl?.displayType || 'DEFAULT',
          });
          nextId++;
        }
      });
      setCommentList((prev) =>
        markDisabled(prev, selectedItems.comments ?? [], 'commentId')
      );
    }

    // Convert metadata fields
    if (selectedItems.metaDatas?.length) {
      selectedItems.metaDatas.forEach((metaId) => {
        const meta = metaDataList?.data?.metadataFieldList.find(
          (m) => m.projectFieldID.toString() === metaId
        );
        if (meta) {
          // Check if this field exists in the API response
          const apiControl = getControlFromAPI(
            'FIELD',
            String(meta.projectFieldID)
          );
          newItems.push({
            id: nextId,
            cfItemID: nextId,
            sequence: nextId,
            type: 'FIELD',
            specialFlag: false,
            value: String(meta.projectFieldID),
            systemLabel: `FIELD_${meta.projectFieldID}`,
            friendlyLabel: meta.projectFieldName,
            logicRuleCount: apiControl?.logicRuleCount || '0',
            tagType: apiControl?.codingFormControlTypeCode || '',
            errorMessage: apiControl?.errorMessage || '',
            defaultAction: apiControl?.defaultAction || 'OPTIONAL',
            helpText: apiControl?.helpText || '',
            fieldLogic: [],
            fieldLogicJson: apiControl?.controlLogicJSon || '[]',
            fieldLogicAction: apiControl?.fieldLogicAction || '',
            fieldLogicObjectLabel: `${meta.projectFieldName} (FIELD_${meta.projectFieldID})`,
            groupAssociation: apiControl?.groupControlName
              ? {
                  value: apiControl.groupControlName,
                  label:
                    apiControl.customControlLabel ||
                    apiControl.groupControlName,
                }
              : null,
            displayType: meta.projectFieldDataTypeControl || 'DEFAULT',
            metaDataType: meta.projectFieldDatatype,
            maxLength: meta.projectFieldMaxLength,
            minValue: meta.projectFieldMinValue,
            maxValue: meta.projectFieldMaxValue,
            splitLength: meta.projectFieldSplitLength,
            dataTypeLengthCode: meta.projectFieldDataTypeLengthCode,
          });
          nextId++;
        }
      });
      setMetadataFieldList((prev) =>
        markDisabled(prev, selectedItems.metaDatas ?? [], 'projectFieldID')
      );
    }

    // Convert special objects
    if (selectedItems.specialObjects?.length) {
      selectedItems.specialObjects.forEach((objId) => {
        const special = specialObjects()?.find((s) => s.id === objId);
        if (special) {
          newItems.push({
            id: nextId,
            cfItemID: nextId,
            sequence: nextId,
            type: special.type.toUpperCase(),
            specialFlag: true,
            value: String(nextId),
            systemLabel: `${special.type.toLowerCase()}_${nextId}`,
            friendlyLabel: special.label,
            logicRuleCount: '0',
            tagType: '',
            errorMessage: '',
            defaultAction: 'OPTIONAL',
            helpText: '',
            fieldLogic: [],
            fieldLogicJson: '[]',
            fieldLogicAction: '',
            fieldLogicObjectLabel: `${special.label} (${special.type.toLowerCase()}_${nextId})`,
            groupAssociation: null,
            displayType: 'DEFAULT',
          });
          nextId++;
        }
      });
    }

    setSelectedObjects((prev) => [...prev, ...newItems]);
    handleFormChange();
  };

  interface HasId {
    [key: string]: any;
    disabled?: boolean;
  }

  const markDisabled = useCallback(
    <T extends HasId>(
      list: T[],
      idSet: Id[],
      idKey: keyof T = 'id',
      isRemove = false
    ): T[] => {
      const set = new Set(idSet.map(String));
      return list.map((item: T) => {
        if (set.has(String(item[idKey]))) {
          if (isRemove) {
            return {...item, disabled: false};
          }
          return {...item, disabled: true};
        }
        return item;
      });
    },
    []
  );

  const removeCodingForm = (item: CodingFormItem) => {
    // Store original ID based on type
    const originalId = item.value;

    // Only remove if we have a valid ID
    if (originalId) {
      setSelectedObjects((prev) =>
        prev.filter((selObj) => selObj.systemLabel !== item.systemLabel)
      );

      // Enable the item in the appropriate list
      if (item.type.toLowerCase() === 'tag') {
        setTagDataList((prev) => markDisabled(prev, [originalId], 'id', true));
      } else if (item.type === 'COMMENT') {
        setCommentList((prev) =>
          markDisabled(prev, [originalId], 'commentId', true)
        );
      } else if (item.type === 'FIELD') {
        setMetadataFieldList((prev) =>
          markDisabled(prev, [originalId], 'projectFieldID', true)
        );
      }
    }

    handleFormChange(); // Ensure form state is updated
  };

  const handleFormChange = useCallback(() => {
    setHasChanges(true);
    onChangeStatus?.(true);
  }, [onChangeStatus]);

  // Make sure resetForm is defined before handleClose
  const resetForm = useCallback(() => {
    setHasChanges(false);
    onChangeStatus?.(false);
    setSelectedObjects([]);
    setTagDataList(metaDataList?.data?.tagDataList ?? []);
    setCommentList(metaDataList?.data?.commentList ?? []);
    setMetadataFieldList(metaDataList?.data?.metadataFieldList ?? []);
  }, [metaDataList, onChangeStatus]);

  // Then update handleClose to use resetForm
  const handleClose = useCallback(() => {
    if (hasChanges) {
      setShowUnsavedModal(true);
    } else {
      resetForm();
      onClose?.();
    }
  }, [hasChanges, onClose, resetForm]);

  const editorRef = useRef<any>(null);

  const handleSave = useCallback(async () => {
    if (!hasChanges) {
      resetForm();
      return;
    }

    if (editorRef.current && typeof editorRef.current.save === 'function') {
      await editorRef.current.save();
    } else if (onSave) {
      await onSave();
    }
    resetForm();
  }, [hasChanges, onSave, resetForm]);

  const handleDiscard = useCallback(() => {
    resetForm();
    onClose?.();
  }, [onClose, resetForm]);

  useImperativeHandle(
    ref,
    () => ({
      save: async () => {
        await handleSave();
      },
      resetDirty: () => {
        setHasChanges(false);
        onChangeStatus?.(false);
        if (editorRef.current?.resetDirty) {
          editorRef.current.resetDirty();
        }
      },
      handleClose,
      forceReset: resetForm,
    }),
    [handleSave, handleClose, resetForm, onChangeStatus]
  );

  return (
    <>
      <Grid container spacing={1} className="create-edit-coding-form-container">
        <Grid size={5}>
          <AvailableObjects
            tagDataList={tagDataList}
            commentList={commentList}
            metadataFieldList={metadataFieldList}
            isLoading={isMetaDataLoading}
            addSelectedToForm={handleAddToForm}
          />
        </Grid>
        <Grid size={7}>
          <CodingFormEditor
            ref={editorRef}
            codingForm={codingForm}
            selectedObjects={selectedObjects}
            removeCodingForm={removeCodingForm}
            onChange={handleFormChange}
            onSave={() => {
              setHasChanges(false);
              onChangeStatus?.(false);
            }}
          />
        </Grid>
      </Grid>

      <UnsavedChangesModal
        showModal={showUnsavedModal}
        onClose={() => setShowUnsavedModal(false)}
        onSave={async () => {
          try {
            await handleSave();
            setShowUnsavedModal(false);
            onClose?.();
          } catch (err) {
            setShowUnsavedModal(false);
            console.error('Save from modal failed', err);
            throw err;
          }
        }}
        onDiscard={() => {
          handleDiscard();
          setShowUnsavedModal(false);
        }}
      />
    </>
  );
});

export default CreateEditCodingForm;
